import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {UploadFile} from '../../documents/upload-file';
import {EntityKey} from '../../entity/entity';
import {SelectionItem} from '../../selections/selection-item';
import {DocumentService} from '../../services/document-service';
import {SelectionService} from '../../services/selection-service';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogRef} from '@angular/material/dialog';
import {StringUtils} from '../../utils/string-utils';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

import {BrandID} from "../../enums/brand-id";

@Component({
    selector: 'document-upload-dialog',
    templateUrl: './document-upload-dialog.component.html',
    styleUrls: ['./document-upload-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, FormsModule, MatDialogActions, MatButton]
})
export class DocumentUploadDialogComponent {
  private readonly documentService = inject(DocumentService);
  private readonly selectionService = inject(SelectionService);
  readonly dialogRef = inject<MatDialogRef<DocumentUploadDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);


  public entityKey: EntityKey;
  readonly uploadFiles = signal<UploadFile[]>([]);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly documentTypes = signal<SelectionItem[]>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);

  constructor
  () {
    const data = this.data;

    this.entityKey = data.entityKey as EntityKey;
    let brandID = data.brandID as BrandID;
    const isCredit = data.documentType === 'credit';
    if (isCredit) {
      this.selectionService.getCreditDocumentTypes(brandID).subscribe(result => {
        this.documentTypes.set(result);
      });
    } else {
      this.selectionService.getDocumentTypes().subscribe(result => {
        this.documentTypes.set(result);
        this.documentTypes()[0].description = ' -- Please select -- ';
      });
    }
  }

  fileChange(upload: any) {
    if (upload.target.files && upload.target.files.length > 0) {
      for (const file of upload.target.files) {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          const mimeType = file.type || this.getMimeType(file.name);
          const uploadFile = new UploadFile();
          uploadFile.documentName = file.name;
          uploadFile.entityKey = this.entityKey;
          uploadFile.fileBytes = reader.result as unknown as Uint8Array;
          uploadFile.fileName = file.name;
          uploadFile.mimeType = mimeType;
          this.uploadFiles.update(current => [...current, uploadFile]);
        };
      }
    }
  }

  private getMimeType(fileName: string): string {
    let mimeType = 'text/plain';
    if (fileName && fileName.length > 2) {
      switch (fileName.slice(-3).toLowerCase()) {
        case 'eml':
          mimeType = 'message/rfc822';
          break;
      }
    }
    return mimeType;
  }

  public saveAndClose(): void {
    this.isBusy.set(true);
    this.errorMessage.set("");
    this.documentService.uploadFiles(this.uploadFiles()).subscribe(() => {
      this.dialogRef.close(true);
    }, error => this.handleError(error));
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public isReadyToUpload(): boolean {
    return this.uploadFiles().length > 0
      && this.uploadFiles().filter(file => !file.documentType || file.documentType === 'NONE').length === 0;
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }
}
